import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { WorkflowTemplate } from 'app/models/workflow-template';
import { Globals } from 'app/services/globals'
import { AcceptanceMethod, AcceptancePercentType, WorkflowStepCompletion } from 'app/models/workflow-step-completion';
import { WorkflowAutoUpload } from 'app/models/workflow-auto-upload';
import { WorkflowAutoEmail } from 'app/models/workflow-auto-email';
import { FormBuilder, Validators, FormGroup, FormArray, ValidatorFn, AbstractControl, ValidationErrors, AsyncValidatorFn, FormControl } from '@angular/forms';
import { ScopeService } from './scope.service';
import { WorkflowStep, StepType, MaterialVisibility, CompletionCriteriaType } from 'app/models/workflow-step';
import { WorkflowFile, WorkflowFileType } from 'app/models/workflow-file';
import { formGroupPatchValueNullFriendly, getDefaultTimezone, loopOnComponents } from 'app/helpers/utils';
import { WorkflowForm } from 'app/models/workflow-form';
import { Observable, of, timer } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { FileClass } from 'app/models/file-class';
import { AutoStartFileSettings, AutoStartFormFieldSettings, AutoStartFormSettings, WorkflowAutoStart } from 'app/models/workflow-auto-start';
import { WorkflowStepExternalOperation } from 'app/models/workflow-step-external-operation';
import { ExternalOperationService } from './external-operation.service';
import { FileExplorerService } from './file-explorer.service';

@Injectable({ providedIn: 'root' })
export class WorkflowTemplateService {

    constructor(private http: HttpClient, private globals: Globals,  private formBuilder: FormBuilder, 
        private scopeService: ScopeService, private externalOperationService: ExternalOperationService,
        private fileExplorerService: FileExplorerService) { }

    workflowTemplate(): WorkflowTemplate{
        return {
            id:null,
            name:'',
            description:'',
            deleted_at: undefined,
            initial_step_completion: this.initStepCompletion({name: 'Start'} as WorkflowStepCompletion),
            author_user:null,
            observers_users_variable: null,
            observers_teams_variable: null,
            is_draft: true,
            is_latest: true,
            version: 0,
            observers_scope_id: null,
            observers_scope: this.scopeService.initScope(),
            is_workflow_exec_name_dynamic: false,
            workflow_exec_name_variable: null,
            tags: [],
        }
    }
   
    getAll(includeDeactivated = false, forExecution: boolean = false) {
        var options:any = { 'include_deactivated': (includeDeactivated)? '1': '0', 'for_execution': (forExecution)? '1': '0'};
        return this.http.get<WorkflowTemplate[]>(`${this.globals.api_base_url}/workflow-templates`, {params: options});
    }

    getById(templateId: number, forExecution: boolean = false) {
      var options:any = { 'for_execution': (forExecution)? '1': '0'};
      return this.http.get<WorkflowTemplate>(`${this.globals.api_base_url}/workflow-template/${templateId}`, {params: options});
    }

    getOrCreateDraft(templateId: number) {
      var options:any = { };
      return this.http.get<WorkflowTemplate>(`${this.globals.api_base_url}/workflow-template/${templateId}/get_or_create_draft`, {params: options});
    }

    put(data: WorkflowTemplate) {
        return this.http.put<WorkflowTemplate>(`${this.globals.api_base_url}/workflow-template`, data);
    }

    publish(workflowTemplateDraftId: number) {
      return this.http.post(`${this.globals.api_base_url}/workflow-template/${workflowTemplateDraftId}/publish`, {});
    }

    deactivate(id: number) {
        return this.http.delete(`${this.globals.api_base_url}/workflow-template/${id}`);
    }

    restore(id: number) {
        return this.http.post(`${this.globals.api_base_url}/workflow-template/${id}/restore`, {});
    }

    listVersions(id: number) {
      return this.http.get<WorkflowTemplate[]>(`${this.globals.api_base_url}/workflow-template/${id}/versions`, {});
    }

    restoreVersion(id: number, version: number) {
      return this.http.post(`${this.globals.api_base_url}/workflow-template/${id}/version/${version}/restore`, {});
    }

    getRecents() {
      return this.http.get<WorkflowTemplate[]>(`${this.globals.api_base_url}/workflow-template/recents`);  
    }

    isNameUnique(name: string, except_id?: number) {
      return this.http.get(`${this.globals.api_base_url}/workflow-template/is-name-unique`, {
        params: {
          name: name,
          except_id: (except_id)? except_id.toString(): ''
        }
      });
    }
    workflowTemplateFormGroup(workflowTemplate: WorkflowTemplate, forExecution: boolean = false) {
      let formGroupControls: any = {
        id: [workflowTemplate.id || undefined],
        name: [workflowTemplate.name || '', {validators: [Validators.required, this.fileExplorerService.illegalFolderCharsValidator], asyncValidators: this.workflowUniqueNameValidator(), updateOn: 'submit'}],
        description: [workflowTemplate.description || ''],
        tags: [[]],
        is_draft: [workflowTemplate.is_draft],
        is_latest: [workflowTemplate.is_latest],
        version: [workflowTemplate.version],
        deleted_at: [undefined],
        observers_scope_id: [undefined],
        observers_scope: this.scopeService.getScopeFormGroup(workflowTemplate.observers_scope),
        observers_users_variable: [workflowTemplate.observers_users_variable],
        observers_teams_variable: [workflowTemplate.observers_teams_variable],
        author_user: [undefined],
        initial_step_completion: this.getStepCompletionFormGroup(workflowTemplate.initial_step_completion, forExecution),
        is_workflow_exec_name_dynamic: [workflowTemplate.is_workflow_exec_name_dynamic],
        workflow_exec_name_variable: [workflowTemplate.workflow_exec_name_variable]
      };
      
      if (forExecution) {
        formGroupControls.variable_observers_scope = this.scopeService.getScopeFormGroup(workflowTemplate.observers_scope);
      }

      return this.formBuilder.group(formGroupControls, { validators: [this.execNameDynamicValidator(), this.workflowObserversValidator()]});
    }

    // init a step completion property with default values or by the values already in the Step completion in the Edit mode
    initStepCompletion(stepCompletion: WorkflowStepCompletion): WorkflowStepCompletion {
        return {
            auto_starts: stepCompletion.auto_starts && stepCompletion.auto_starts.length > 0 ? stepCompletion.auto_starts : [],
            auto_emails: stepCompletion.auto_emails && stepCompletion.auto_emails.length > 0 ? stepCompletion.auto_emails : [],
            auto_uploads: stepCompletion.auto_uploads && stepCompletion.auto_uploads.length > 0 ? stepCompletion.auto_uploads : [],
            files: stepCompletion.files && stepCompletion.files.length > 0 ? stepCompletion.files : [],
            forms: stepCompletion.forms && stepCompletion.forms.length > 0 ? stepCompletion.forms : [],
            next_step: stepCompletion.next_step ? stepCompletion.next_step : null,
            id: stepCompletion.id && stepCompletion.id > 0 ? stepCompletion.id : undefined,
            name: stepCompletion.name && stepCompletion.name != '' ? stepCompletion.name : '',
            should_confirm: stepCompletion.should_confirm ? stepCompletion.should_confirm : false,
            allow_additional_files: stepCompletion.allow_additional_files? stepCompletion.allow_additional_files: false,
            allow_additional_forms: stepCompletion.allow_additional_forms? stepCompletion.allow_additional_forms: false,
            step: stepCompletion.step ? stepCompletion.step : null,
            required_user_auths: stepCompletion.required_user_auths? stepCompletion.required_user_auths: null,
            priority: stepCompletion.priority || 1,
            is_enabled_acceptance_criteria: stepCompletion.is_enabled_acceptance_criteria || false,
            acceptance_method: stepCompletion.acceptance_method || AcceptanceMethod.ACCEPTANCE_METHOD_PLURALITY,
            acceptance_people: stepCompletion.acceptance_people || 1,
            acceptance_percent: stepCompletion.acceptance_percent || 100,
            acceptance_percent_type: stepCompletion.acceptance_percent_type || AcceptancePercentType.ACCEPTANCE_PERCENT_TYPE_NOTIFIED
        }
    }

    getStepCompletionFormGroup(workflowStepCompletion: WorkflowStepCompletion, forExecution: boolean = false) {
        let files = this.formBuilder.array([]);
        if(workflowStepCompletion.files) {
            workflowStepCompletion.files.forEach( f => {
                files.push(this.getWorkflowFileFormGroup(f, null, forExecution));
            });
        }

        let forms = this.formBuilder.array([]);
        if(workflowStepCompletion.forms) {
            workflowStepCompletion.forms.forEach( f => {
                forms.push(this.getWorkflowFormFormGroup(f, null, forExecution));
            });
        }

        let autoUploads = this.formBuilder.array([])
        if(workflowStepCompletion.auto_uploads) {
            workflowStepCompletion.auto_uploads.forEach( a => {
                autoUploads.push(this.getWorkflowAutoUploadFormGroup(a, forExecution));
            });
        }

        let autoEmails = this.formBuilder.array([])
        if(workflowStepCompletion.auto_emails) {
            workflowStepCompletion.auto_emails.forEach( a => {
                autoEmails.push(this.getWorkflowAutoEmailFormGroup(a, forExecution));
            });
        }

        let autoStarts = this.formBuilder.array([])
        if(workflowStepCompletion.auto_starts) {
            workflowStepCompletion.auto_starts.forEach( a => {
                autoStarts.push(this.getWorkflowAutoStartFormGroup(a));
            });
        }

        let formControls: any = {
          id: [undefined],
          name: [workflowStepCompletion.name, [Validators.required, this.illegalStepAndStepCompletionCharsValidator, this.workflowStepCompletionUniqueNameValidator()]],
          should_confirm: [workflowStepCompletion.should_confirm? workflowStepCompletion.should_confirm: false],
          allow_additional_forms: [workflowStepCompletion.allow_additional_forms? workflowStepCompletion.allow_additional_forms: false],
          allow_additional_files: [workflowStepCompletion.allow_additional_files? workflowStepCompletion.allow_additional_files: false],
          next_step: (workflowStepCompletion.next_step)? this.createStepFormGroup(workflowStepCompletion.next_step, null, forExecution): null,
          step: [undefined],
          auto_emails: autoEmails,
          auto_uploads: autoUploads,
          auto_starts: autoStarts,
          files: files,
          forms: forms,
          required_user_auths: workflowStepCompletion.required_user_auths,
          priority: [workflowStepCompletion.priority || 1],
          is_enabled_acceptance_criteria: [workflowStepCompletion.is_enabled_acceptance_criteria || false],
          acceptance_method: [workflowStepCompletion.acceptance_method || AcceptanceMethod.ACCEPTANCE_METHOD_PLURALITY],
          acceptance_people: [workflowStepCompletion.acceptance_people || 1, [Validators.min(1)]],
          acceptance_percent: [workflowStepCompletion.acceptance_percent || 100, [Validators.max(100), Validators.min(1)]],
          acceptance_percent_type: [workflowStepCompletion.acceptance_percent_type || AcceptancePercentType.ACCEPTANCE_PERCENT_TYPE_NOTIFIED]
        };

        return this.formBuilder.group(formControls);
    }

    initWorkflowStep(newStepCompletionsCount: number, newStepType?: StepType) {
        let newStep: WorkflowStep;
        switch (newStepType) {
            case StepType.REVIEW:
                newStep = {
                    name: '',
                    step_completions: [
                      this.initStepCompletion({ name: 'Done' } as WorkflowStepCompletion),
                    ],
                    workflow_step_type_id: StepType.REVIEW,
                    previous_materials_visibility: MaterialVisibility.MATERIAL_VISIBILITY_SHOW_ALL,
                    completion_criteria_type: CompletionCriteriaType.COMPLETION_CRITERIA_TYPE_FIRST_ONE
                };
        
                break;
            case StepType.APPROVAL:
                newStep = {
                    name: '',
                    step_completions: [
                      this.initStepCompletion({ name: 'Approve', should_confirm: true } as WorkflowStepCompletion),
                      this.initStepCompletion({ name: 'Reject' } as WorkflowStepCompletion)
                    ],
                    workflow_step_type_id: StepType.APPROVAL,
                    previous_materials_visibility: MaterialVisibility.MATERIAL_VISIBILITY_SHOW_ALL,
                    completion_criteria_type: CompletionCriteriaType.COMPLETION_CRITERIA_TYPE_FIRST_ONE
                };
                break;
    
            default:
                newStep = {
                    name: '',
                    step_completions: [],
                    workflow_step_type_id: newStepType,
                    previous_materials_visibility: MaterialVisibility.MATERIAL_VISIBILITY_SHOW_ALL,
                    completion_criteria_type: CompletionCriteriaType.COMPLETION_CRITERIA_TYPE_FIRST_ONE
                };
                for (var i = 0; i < newStepCompletionsCount; i++) {
                    newStep.step_completions.push(
                    this.initStepCompletion({ name: `Decision ${i+1}` } as WorkflowStepCompletion))
                }
                break;
        }

        return newStep;
    }    

    illegalStepAndStepCompletionCharsValidator(c: FormControl) {
      let regex = /[:,]/g;
      if (regex.test(c.value)) {
        return { stepIllegalChars: true }
      } else {
        return null;
      }
    }

    createStepFormGroup(step: WorkflowStep, parentStepCompletionFormGroup: FormGroup = null, forExecution: boolean = false): FormGroup {
        let stepCompletions = this.formBuilder.array([])
        if(step.step_completions.length > 0) {
            step.step_completions.forEach( sc => {
                stepCompletions.push(this.getStepCompletionFormGroup(sc, forExecution));
            });
        }

        let formControls: any = {
          id: [undefined],
          name: [step.name, [Validators.required, this.illegalStepAndStepCompletionCharsValidator, this.workflowStepUniqueNameValidator()]],
          description: [step.description, [Validators.required]],
          due_after: [step.due_after],
          timeout_after: [step.timeout_after],
          script: [step.script],
          target_scope: this.scopeService.getScopeFormGroup(step.target_scope),
          target_users_variable: [step.target_users_variable],
          target_teams_variable: [step.target_teams_variable],
          target_contacts_variable: [step.target_contacts_variable],
          parent_step_completion: [undefined],
          workflow_step_type_id: [step.workflow_step_type_id],
          step_completions: stepCompletions,
          workflow_step_external_operations: [step.workflow_step_external_operations],
          previous_materials_visibility: [step.previous_materials_visibility || MaterialVisibility.MATERIAL_VISIBILITY_SHOW_ALL],
          is_hide_history: [step.is_hide_history || false],
          completion_criteria_type: [step.completion_criteria_type || CompletionCriteriaType.COMPLETION_CRITERIA_TYPE_FIRST_ONE],
          completion_percent: [step.completion_percent || 100, [Validators.max(100), Validators.min(1)]],
          completion_people: [step.completion_people || 1, [Validators.min(1)]],
          default_step_completion_id: [step.default_step_completion_id],
          default_step_completion: [step.default_step_completion],
          visible_workflow_files: [step.visible_workflow_files],
          visible_workflow_forms: [step.visible_workflow_forms]
        };

        let stepFormGroup = this.formBuilder.group(formControls, { validators: this.workflowStepValidator(forExecution)});

        if (parentStepCompletionFormGroup) {
            parentStepCompletionFormGroup.controls['next_step'] = stepFormGroup;
            stepFormGroup.setParent(parentStepCompletionFormGroup);
            parentStepCompletionFormGroup.controls['next_step'].updateValueAndValidity();
            return parentStepCompletionFormGroup.controls['next_step'] as FormGroup;
        }

        return stepFormGroup;
    }

    addNewStepCompletionToStep(stepFormGroup: FormGroup, stepCompletionToAdd: WorkflowStepCompletion = null) {
      let newStepCompletionFormGroup = this.getStepCompletionFormGroup(stepCompletionToAdd || this.initStepCompletion({
        name: 'New Decision'
      } as WorkflowStepCompletion));
      stepFormGroup.get('workflow_step_type_id').setValue(stepFormGroup.get('workflow_step_type_id').value == StepType.AUTO? StepType.AUTO: StepType.CUSTOM);
  
      (stepFormGroup.get('step_completions') as FormArray).push(newStepCompletionFormGroup);
      newStepCompletionFormGroup.setParent((stepFormGroup.get('step_completions') as FormArray));
      (stepFormGroup.get('step_completions') as FormArray).at((stepFormGroup.get('step_completions') as FormArray).length - 1).updateValueAndValidity();

      return newStepCompletionFormGroup;
    }
  
    deleteStepCompletion(stepCompletionFormGroup: FormGroup) {
      let stepCompletionFormArray: FormArray = stepCompletionFormGroup.parent as FormArray;
      stepCompletionFormArray.removeAt(stepCompletionFormArray.controls.findIndex((v)=> v == stepCompletionFormGroup));  
    }

    deleteStep(stepFormGroup: FormGroup, stepCompletionIndexToKeep: number = -1) {
      let parentStepCompletion: FormGroup = stepFormGroup.parent as FormGroup;
    
      let attachedStepFormGroup = new FormGroup({});
      if (stepCompletionIndexToKeep != -1) {
        attachedStepFormGroup = <FormGroup>stepFormGroup.get('step_completions.' + stepCompletionIndexToKeep + '.next_step');
      }

      parentStepCompletion.controls['next_step'] = attachedStepFormGroup;
      attachedStepFormGroup.setParent(parentStepCompletion as FormGroup);
      attachedStepFormGroup.updateValueAndValidity();
    }

    workflowStepExternalOperationFormGroup(workflowStepExternalOperation: WorkflowStepExternalOperation = null) {
      let frmGrp: FormGroup = this.formBuilder.group({
          id: [(workflowStepExternalOperation)? workflowStepExternalOperation.id: null],
          unique_name: [(workflowStepExternalOperation)? workflowStepExternalOperation.unique_name: null, Validators.required],
          external_operation_common_settings: this.externalOperationService.externalOperationCommonSettingsFormGroup((workflowStepExternalOperation)? workflowStepExternalOperation.external_operation_common_settings: null)
      });        
      return frmGrp;
    }

    getWorkflowFileFormGroup(file: WorkflowFile, stepCompletionFormGroup? : FormGroup, forExecution: boolean = false) {
        return this.formBuilder.group({
            id: [undefined],
            name: [file.name, forExecution? [Validators.required] : [Validators.required, this.workflowFileUniqueNameValidator(stepCompletionFormGroup, file.name)]],
            description: [file.description, Validators.maxLength(255)],
            isAdditional: [file.isAdditional],
            attached_file_path: [file.attached_file_path, Validators.maxLength(2048)],
            attached_storage_id: [file.attached_storage_id],
            file_type_id: [file.file_type_id? file.file_type_id: WorkflowFileType.FILE_TYPE_NEW, Validators.required],
            is_duplicate_per_target: [file.is_duplicate_per_target || false]
        });
    }

    getWorkflowFormFormGroup(form: WorkflowForm, stepCompletionFormGroup? : FormGroup, forExecution: boolean = false) {
        return this.formBuilder.group({
            id: [undefined],
            name: [form.name, forExecution? [Validators.required] : [Validators.required, this.workflowFormUniqueNameValidator(stepCompletionFormGroup, form.is_existing? null: form.name)]],
            description: [form.description, Validators.maxLength(255)],
            form_template_id: [form.form_template_id, Validators.required],
            required_fields: [form.required_fields],
            optional_fields: [form.optional_fields],
            form_template: [form.form_template],
            isAdditional: [form.isAdditional],
            is_existing: [form.is_existing? 1: 0],
            is_duplicate_per_target: [form.is_duplicate_per_target || false]
        });
    }

    generateMetadataFormArray(fileClass: FileClass) {
      if (!fileClass) {
        return this.formBuilder.array([]);
      } else {
        let metadataFormArray = this.formBuilder.array([]);
        fileClass.required_metadata.forEach((m) => {
          m.schema.validate = m.schema.validate || {};
          m.schema.validate.required = true;
          metadataFormArray.push(this.formBuilder.group({
            id: [m.id],
            name: [m.name],
            type: [m.type],
            schema: [m.schema],
            key: [m.key],
            auto_upload_metadata_settings: this.formBuilder.group({
              is_required: [1],
              is_dynamic_value: [0],
              value: ['', Validators.required],
              timezone: [getDefaultTimezone()]
            })
          }));
        }); 

        fileClass.optional_metadata.forEach((m) => {
          metadataFormArray.push(this.formBuilder.group({
            id: [m.id],
            name: [m.name],
            type: [m.type],
            schema: [m.schema],
            key: [m.key],
            auto_upload_metadata_settings: this.formBuilder.group({
              is_required: [0],
              is_dynamic_value: [0],
              value: [''],
              timezone: [getDefaultTimezone()]
            })
          }));
        }); 
        
        return metadataFormArray;
      }
    }

    getAutoStartFileFormGroup(file: AutoStartFileSettings) {
      return this.formBuilder.group({
        linked_workflow_file_name: [file.linked_workflow_file_name, [Validators.required]],
        initiator_workflow_file_name: [file.initiator_workflow_file_name, [Validators.required]],
        description: [file.description],
        is_additional_file: [file.is_additional_file]
      });
    }

    getAutoStartFormFormGroup(form: AutoStartFormSettings) {
      let submissionValueFormArray = this.formBuilder.array([]);
      if (form.submission_value) {
        form.submission_value.forEach((f) => {
          submissionValueFormArray.push(this.formBuilder.group({
            field_key: [f.field_key, Validators.required],
            field_label: [f.field_label],
            field_type: [f.field_type],
            is_required: [f.is_required],
            exec_variable: [f.exec_variable]
          }, {validators: [this.autoStartSubmissionValueValidator()]}));
        });
      }

      return this.formBuilder.group({
        linked_workflow_form_name: [form.linked_workflow_form_name, [Validators.required]],
        linked_workflow_form_path: [form.linked_workflow_form_path],
        initiator_workflow_form_name: [form.initiator_workflow_form_name],
        description: [form.description],
        is_additional_form: [form.is_additional_form],
        is_entire_form: [form.is_entire_form],
        submission_value: submissionValueFormArray
      }, { validators: [this.autoStartFormValidator()]});
    }

    generateWorkflowAutoStartFormGroup(workflowTemplate: WorkflowTemplate) {
      
      let filesFormArray = this.formBuilder.array([]);
      if (workflowTemplate.initial_step_completion.files) {
        workflowTemplate.initial_step_completion.files.forEach((f) => {
          filesFormArray.push(this.formBuilder.group({
            linked_workflow_file_name: [f.name, [Validators.required]],
            initiator_workflow_file_name: ['', [Validators.required]],
            description: [f.description],
            is_additional_file: [0]
          }));
        });
      }

      let formsFormArray = this.formBuilder.array([]);
      if (workflowTemplate.initial_step_completion.forms) {
        workflowTemplate.initial_step_completion.forms.forEach((f) => {

          let submissionValueFormArray = this.formBuilder.array([]);
          loopOnComponents(f.form_template.schema, (cmp, submissionPrefix) => {
            if (cmp.input == true && cmp.type != 'button' && cmp.type != 'datagrid') {
              let fullKey: string = submissionPrefix + cmp.key;
              let isSkip: boolean = false;
              let isRequired: boolean = false;
              if (f.required_fields) {
                if (f.required_fields.includes(fullKey)) {
                  isRequired = true;
                } else if (f.optional_fields && f.optional_fields.includes(fullKey)) {
                  isSkip = false;
                } else {
                  isSkip = true;
                }
              } else {
                isRequired = cmp && cmp.validate && cmp.validate.required;
              }

              if (!isSkip) {
                submissionValueFormArray.push(this.formBuilder.group({
                  field_key: [fullKey, Validators.required],
                  field_label: [cmp.label],
                  field_type: [cmp.type],
                  is_required: [(isRequired)? 1: 0],
                  exec_variable: [null]
                }, {validators: [this.autoStartSubmissionValueValidator()]}));
              }
            }
          });

          formsFormArray.push(this.formBuilder.group({
            linked_workflow_form_path: [f.form_template.path],
            linked_workflow_form_name: [f.name, [Validators.required]],
            initiator_workflow_form_name: [null],
            description: [f.description],
            is_additional_form: [0],
            is_entire_form: [1],
            submission_value: submissionValueFormArray
          }, {validators: [this.autoStartFormValidator()]}));
        });
      }

      return this.formBuilder.group({
        workflow_template: [{id: workflowTemplate.id, name: workflowTemplate.name, initial_step_completion: workflowTemplate.initial_step_completion, description: workflowTemplate.description}],
        files: filesFormArray,
        forms: formsFormArray
      });
    }

    getWorkflowAutoStartFormGroup(autoStart: WorkflowAutoStart) {
      
      let filesFormArray = this.formBuilder.array([]);
      if (autoStart.files) {
        autoStart.files.forEach((file) => {
          filesFormArray.push(this.getAutoStartFileFormGroup(file));
        });
      }

      let formsFormArray = this.formBuilder.array([]);
      if (autoStart.forms) {
        autoStart.forms.forEach((form) => {
          formsFormArray.push(this.getAutoStartFormFormGroup(form));
        });
      }

      let autoStartControls: any = {
        id: [undefined],
        workflow_template: [autoStart.workflow_template],
        files: filesFormArray,
        forms: formsFormArray
      };
      
      return this.formBuilder.group(autoStartControls);
    }

    getWorkflowAutoUploadFormGroup(autoUpload: WorkflowAutoUpload, forExecution: boolean = false) {
      
      let additionalPathsFormArray = this.formBuilder.array([]);
      let additionalPaths = autoUpload.additional_paths || [];
      additionalPaths.forEach((s) => {
        additionalPathsFormArray.push(this.formBuilder.control(s || '', [Validators.required]));
      });

      let metadataFormArray = this.formBuilder.array([]);
      let metadata = autoUpload.metadata || [];
      metadata.forEach((m) => {
        m.schema.validate = m.schema.validate || {};
        m.schema.validate.required = (m.auto_upload_metadata_settings && m.auto_upload_metadata_settings.is_required);
        metadataFormArray.push(this.formBuilder.group({
          id: [m.id],
          name: [m.name],
          type: [m.type],
          schema: [m.schema],
          key: [m.key],
          auto_upload_metadata_settings: this.formBuilder.group({
            is_required: [(m.auto_upload_metadata_settings && m.auto_upload_metadata_settings.is_required)? 1: 0],
            is_dynamic_value: [(m.auto_upload_metadata_settings && m.auto_upload_metadata_settings.is_dynamic_value)? 1: 0],
            value: [(m.auto_upload_metadata_settings && m.auto_upload_metadata_settings.value)? m.auto_upload_metadata_settings.value: '', (m.auto_upload_metadata_settings && m.auto_upload_metadata_settings.is_required)? Validators.required: null],
            timezone: [(m.auto_upload_metadata_settings && m.auto_upload_metadata_settings.timezone)? m.auto_upload_metadata_settings.timezone: getDefaultTimezone()]
          })
        }));
      });

      let formControls: any = {
        id: [undefined],
        path: [autoUpload.path],
        additional_paths: additionalPathsFormArray,
        path_variable: [autoUpload.path_variable],
        should_overwrite: [autoUpload.should_overwrite? 1: 0],
        is_all_files: [autoUpload.is_all_files? autoUpload.is_all_files: false],
        is_all_forms: [autoUpload.is_all_forms? autoUpload.is_all_forms: false],
        file_rename: [autoUpload.file_rename],
        file_name_prefix: [autoUpload.file_name_prefix],
        file_name_suffix: [autoUpload.file_name_suffix],
        files: [autoUpload.files],
        forms: [autoUpload.forms],
        storage_id: [autoUpload.storage_id],
        storage: [autoUpload.storage],
        use_existing_metadata: [autoUpload.use_existing_metadata? 1: 0],
        file_class_id: [autoUpload.file_class_id],
        metadata: metadataFormArray
      };
      
      return this.formBuilder.group(formControls, { validators: this.workflowAutoUploadValidator(forExecution)});
    }

    addAdditionalEmptyPathToAutoUploadRule(autoUploadFormGroup: FormGroup) {
      (<FormArray>autoUploadFormGroup.get('additional_paths')).push(this.formBuilder.control('', [Validators.required]));
    }

    getWorkflowAutoEmailFormGroup(autoEmail: WorkflowAutoEmail, forExecution: boolean = false) {
      let formControls: any = {
        id: [undefined],
        from_user: [autoEmail.from_user],
        from_custom: [autoEmail.from_custom, Validators.email],
        is_from_initiator: [autoEmail.is_from_initiator? autoEmail.is_from_initiator: false],
        from_variable: [autoEmail.from_variable],

        to_users_variable: [autoEmail.to_users_variable],
        to_teams_variable: [autoEmail.to_teams_variable],
        to_contacts_variable: [autoEmail.to_contacts_variable],

        add_initiator_to_to: [autoEmail.add_initiator_to_to? autoEmail.add_initiator_to_to: false],
        to_scope: this.scopeService.getScopeFormGroup(),
        
        subject: [autoEmail.subject, [Validators.required]],        
        body: [autoEmail.body],
        
        add_initiator_to_cc: [autoEmail.add_initiator_to_cc? autoEmail.add_initiator_to_cc: false],
        cc_scope: this.scopeService.getScopeFormGroup(),
        cc_users_variable: [autoEmail.cc_users_variable],
        cc_teams_variable: [autoEmail.cc_teams_variable],
        cc_contacts_variable: [autoEmail.cc_contacts_variable],
        
        add_initiator_to_bcc: [autoEmail.add_initiator_to_bcc? autoEmail.add_initiator_to_bcc: false],
        bcc_scope: this.scopeService.getScopeFormGroup(),
        bcc_users_variable: [autoEmail.bcc_users_variable],
        bcc_teams_variable: [autoEmail.bcc_teams_variable],
        bcc_contacts_variable: [autoEmail.bcc_contacts_variable],
        
        add_targets_to_to: [autoEmail.add_targets_to_to? autoEmail.add_targets_to_to: false],
        add_targets_to_cc: [autoEmail.add_targets_to_cc? autoEmail.add_targets_to_cc: false],
        add_targets_to_bcc: [autoEmail.add_targets_to_bcc? autoEmail.add_targets_to_bcc: false],
        
        forms: [autoEmail.forms],
        files: [autoEmail.files],
        is_all_files: [autoEmail.is_all_files? autoEmail.is_all_files: false],
        is_all_forms: [autoEmail.is_all_forms? autoEmail.is_all_forms: false]
      };
      
      return this.formBuilder.group(formControls, {validators: this.workflowAutoEmailValidator(forExecution)});
    }

    
  // Gets the parent files for this.currentStep and the passed stepCompletionName
  getParentFiles(workflowTemplateFormGroup: FormGroup, stepCompletionName: string, stepName: string, isAddSiblingFiles: boolean = true): WorkflowFile[] {
    let files = this.getStepsFiles((workflowTemplateFormGroup.controls['initial_step_completion'] as FormGroup), null, stepCompletionName, stepName, isAddSiblingFiles);
    if (typeof files != "object")
      files = [];
    return files;
  }

  getStepsFiles(currentStepCompletionFormGroup: FormGroup, currentStepName: string, stepCompletionName: string, stepName: string, isAddSiblingFiles: boolean = true): WorkflowFile[]|boolean {
    let files: WorkflowFile[] | boolean = false;
    
    // Check if that's the step completion we're searching for
    if(currentStepCompletionFormGroup.value.name == stepCompletionName && 
       currentStepName == stepName) {
         if (isAddSiblingFiles) {
          files = (currentStepCompletionFormGroup.controls['files'].value as WorkflowFile[]).filter((w) => w.file_type_id != WorkflowFileType.FILE_TYPE_EXISTING);
         } else {
           files = [];
         }
    } else if(currentStepCompletionFormGroup.controls['next_step'] && currentStepCompletionFormGroup.controls['next_step'].value) {
      var nextStepName = (currentStepCompletionFormGroup.controls['next_step'] as FormGroup).value.name;
      ((currentStepCompletionFormGroup.controls['next_step'] as FormGroup).controls['step_completions'] as FormArray).controls.forEach( (sc: FormGroup) => {
        var childResult = this.getStepsFiles(sc, nextStepName, stepCompletionName, stepName);
        if (typeof childResult == "object") {
          files = childResult;
          files = files.concat(currentStepCompletionFormGroup.controls['files'].value as WorkflowFile[]).filter((w) => w.file_type_id != WorkflowFileType.FILE_TYPE_EXISTING);
        }
      });
    }

    return files;
  }

  // Gets the parent forms for this.currentStep and the passed stepCompletionName
  getParentForms(workflowTemplateFormGroup: FormGroup, stepCompletionName: string, stepName: string, isAddSiblingForms: boolean = true): WorkflowForm[] {
    let forms = this.getStepsForms((workflowTemplateFormGroup.controls['initial_step_completion'] as FormGroup), null, stepCompletionName, stepName, isAddSiblingForms);
    if (typeof forms != "object")
      forms = [];
    return forms;
  }

  getStepsForms(currentStepCompletionFormGroup: FormGroup, currentStepName: string, stepCompletionName: string, stepName: string, isAddSiblingForms: boolean = true): WorkflowForm[]|boolean {
    let forms: WorkflowForm[] | boolean = false;
    
    // Check if that's the step completion we're searching for
    if(currentStepCompletionFormGroup.value.name == stepCompletionName && 
       currentStepName == stepName) {
         if (isAddSiblingForms)
          forms = (currentStepCompletionFormGroup.controls['forms'].value as WorkflowForm[]).filter((w) => w.is_existing == false);
        else
          forms = [];
    } else if(currentStepCompletionFormGroup.controls['next_step'] && currentStepCompletionFormGroup.controls['next_step'].value) {
      var nextStepName = (currentStepCompletionFormGroup.controls['next_step'] as FormGroup).value.name;
      ((currentStepCompletionFormGroup.controls['next_step'] as FormGroup).controls['step_completions'] as FormArray).controls.forEach( (sc: FormGroup) => {
        var childResult = this.getStepsForms(sc, nextStepName, stepCompletionName, stepName, isAddSiblingForms);
        if (typeof childResult == "object") {
          forms = childResult;
          forms = forms.concat((currentStepCompletionFormGroup.controls['forms'].value  as WorkflowForm[]).filter((w) => w.is_existing == false));
        }
      });
    }

    return forms;
  }

  workflowFileUniqueNameValidator(currentStepCompletionFormGroup?: FormGroup, ignoreFileName?: string): ValidatorFn {

    return (control: AbstractControl): ValidationErrors => {
      // Try to resolve the step completion form group from the tree if non is supplied
      if (!currentStepCompletionFormGroup && control.parent && control.parent.parent && control.parent.parent)
        currentStepCompletionFormGroup = control.parent.parent.parent as FormGroup;

      if (currentStepCompletionFormGroup && control.parent && control.value && control.parent.get('file_type_id').value != WorkflowFileType.FILE_TYPE_EXISTING) {
        // Check parent files first
        let allFiles: WorkflowFile[] = [];
        
        // make sure it's not the initial step completion
        if (currentStepCompletionFormGroup.parent && currentStepCompletionFormGroup.parent.parent) {
            allFiles.push(...this.getParentFiles(currentStepCompletionFormGroup.root as FormGroup, currentStepCompletionFormGroup.controls['name'].value,
            (currentStepCompletionFormGroup.parent.parent as FormGroup).controls['name'].value));
        }

        // filter out ourselves
        allFiles = allFiles.filter(v => v != control.parent.value);

        if (allFiles.findIndex((f) => { return f.name == (control.value as string).trim() && !(ignoreFileName && ignoreFileName == (control.value as string).trim());}) != -1) {
          return { 'workflowFileUniqueName': {value: control.value} };
        }
      }
      return null;
    };
  }

  workflowFormUniqueNameValidator(currentStepCompletionFormGroup?: FormGroup, ignoreFormName?: string): ValidatorFn {

    return (control: AbstractControl): ValidationErrors => {
      // Try to resolve the step completion form group from the tree if non is supplied
      if (!currentStepCompletionFormGroup && control.parent && control.parent.parent && control.parent.parent)
        currentStepCompletionFormGroup = control.parent.parent.parent as FormGroup;

      if (currentStepCompletionFormGroup && control.parent && control.value && !control.parent.get('is_existing').value) {
        // Check parent forms first
        let allForms: WorkflowForm[] = [];
        // make sure it's not the initial step completion
        if (currentStepCompletionFormGroup.parent && currentStepCompletionFormGroup.parent.parent) {
            allForms.push(...this.getParentForms(currentStepCompletionFormGroup.root as FormGroup, currentStepCompletionFormGroup.controls['name'].value,
            (currentStepCompletionFormGroup.parent.parent as FormGroup).controls['name'].value));
        }
        
        // filter out ourselves
        allForms = allForms.filter(v => v != control.parent.value);

        if (allForms.findIndex((f) => { return f.name == (control.value as string).trim() && !(ignoreFormName && ignoreFormName == (control.value as string).trim()); }) != -1) {
          return { 'workflowFormUniqueName': {value: control.value} };
        }
      }
      return null;
    };
  }

  workflowStepCompletionUniqueNameValidator(): ValidatorFn {

    return (control: AbstractControl): ValidationErrors => {
      // Try to resolve the step form group from the tree
      let currentStepFormGroup: FormGroup = null;
      if (control.parent && control.parent.parent && control.parent.parent)
        currentStepFormGroup = control.parent.parent.parent as FormGroup;

      if (currentStepFormGroup) {
        if ((currentStepFormGroup.controls['step_completions'] as FormArray).controls.findIndex(
          (c) => { 
            let stepCompletionFormGroup: FormGroup = c as FormGroup;
            return stepCompletionFormGroup != control.parent && stepCompletionFormGroup.controls['name'].value == (control.value as string).trim(); 
          }) != -1) {
          return { 'workflowStepCompletionUniqueName': {value: control.value} };
        }
      }
      return null;
    };
  }

  workflowStepUniqueNameValidator(): ValidatorFn {

    return (control: AbstractControl): ValidationErrors => {

      // Make sure this step is attached to a tree for this validation to work correctly
      if (control.root && control.root.value &&
         control.root.value.initial_step_completion &&
         control.root.value.initial_step_completion.next_step) {

        let isStepNameUnique: (currentStep: AbstractControl, stepToCheckFormGroup: FormGroup) => boolean = 
          (currentStep: AbstractControl, stepToCheckFormGroup: FormGroup) => {
            let currentStepFormGroup: FormGroup = currentStep as FormGroup;
            if (!currentStepFormGroup || !currentStepFormGroup.value || !currentStepFormGroup.value.name)
              return true;

            if (currentStepFormGroup != stepToCheckFormGroup && currentStepFormGroup.controls['name'].value == (stepToCheckFormGroup.controls['name'].value as string).trim()) {
              return false;
            } else {
              // recursively search the tree
              let stepCompletions: AbstractControl[] = (currentStepFormGroup.controls['step_completions'] as FormArray).controls;
              for (let i = 0; i < stepCompletions.length; i++) {
                const sc: FormGroup = stepCompletions[i] as FormGroup;
                if (!isStepNameUnique(sc.controls['next_step'], stepToCheckFormGroup))
                  return false;
              }
            }

            return true;
        };
        if (!isStepNameUnique(((control.root as FormGroup).controls['initial_step_completion'] as FormGroup).controls['next_step'], control.parent as FormGroup)) {
          return { 'workflowStepUniqueName': {value: control.value} };
        }
      }

      return null;
    };
  }

  workflowAutoEmailValidator(forExecution: boolean = false): ValidatorFn {

    return (frm: FormGroup): ValidationErrors => {

      let result: any = {};

      // Check From
      const from_variable = frm.get('from_variable');
      const is_from_initiator = frm.get('is_from_initiator');
      const from_user = frm.get('from_user');
      const from_custom = frm.get('from_custom');
      
      if (from_variable && !from_variable.value && is_from_initiator && !is_from_initiator.value &&
        from_user && (!from_user.value || !from_user.value.id) && from_custom && (!from_custom.value || from_custom.value == '')) {
        result.fromRequired = true;
      }

      // Check To
      const to_users_variable = frm.get('to_users_variable');
      const to_teams_variable = frm.get('to_teams_variable');
      const to_contacts_variable = frm.get('to_contacts_variable');
      const add_initiator_to_to = frm.get('add_initiator_to_to');
      const add_targets_to_to = frm.get('add_targets_to_to');
      const to_scope = frm.get('to_scope');
      
      if (to_users_variable && !to_users_variable.value && to_teams_variable && !to_teams_variable.value && to_contacts_variable && !to_contacts_variable.value && 
          add_targets_to_to && !add_targets_to_to.value &&
          add_initiator_to_to && !add_initiator_to_to.value &&
          to_scope && 
            (!to_scope.value || (!to_scope.value.users || !to_scope.value.users.length)
                              && (!to_scope.value.teams || !to_scope.value.teams.length)
                              && (!to_scope.value.contacts || !to_scope.value.contacts.length)
            )
          ) {
        result.toRequired = true;
      }        
      // Check body
      const body = frm.get('body');

      if (body && (!body.value || body.value == '')) {
        result.bodyRequired = true;
      }

      return (Object.keys(result).length == 0)? null: result;
    };
  }

  workflowStepValidator(forExecution: boolean = false): ValidatorFn {

    return (frm: FormGroup): ValidationErrors => {

      let result: any = {};

      // Check Targets
      const workflow_step_type_id = frm.get('workflow_step_type_id');
      const target_scope = frm.get('target_scope');
      const target_users_variable = frm.get('target_users_variable');
      const target_teams_variable = frm.get('target_teams_variable');
      const target_contacts_variable = frm.get('target_contacts_variable');
      
      if (workflow_step_type_id && workflow_step_type_id.value != StepType.AUTO && target_scope && (
        (!target_scope.value.users || target_scope.value.users.length == 0) &&
        (!target_scope.value.teams || target_scope.value.teams.length == 0) &&
        (!target_scope.value.contacts || target_scope.value.contacts.length == 0)
      ) && target_users_variable && target_users_variable.value == null &&
          target_teams_variable && target_teams_variable.value == null &&
          target_contacts_variable && target_contacts_variable.value == null) {
        result.targetRequired = true;
      }

      return (Object.keys(result).length == 0)? null: result;
    };
  }

  workflowObserversValidator(): ValidatorFn {

    return (frm: FormGroup): ValidationErrors => {

      let result: any = {};

      // Check observerss
      const observers_scope = frm.get('observers_scope');
      const observers_users_variable = frm.get('observers_users_variable');
      const observers_teams_variable = frm.get('observers_teams_variable');
      
      if (observers_scope && (
        (!observers_scope.value.users || observers_scope.value.users.length == 0) &&
        (!observers_scope.value.teams || observers_scope.value.teams.length == 0)
      ) && observers_users_variable && observers_users_variable.value == null &&
          observers_teams_variable && observers_teams_variable.value == null) {
        result.observersRequired = true;
      }

      return (Object.keys(result).length == 0)? null: result;
    };
  }

  workflowAutoUploadValidator(forExecution: boolean = false): ValidatorFn {

    return (frm: FormGroup): ValidationErrors => {

      let result: any = {};

      // make sure we have at least 1 file or form
      const is_all_files = frm.get('is_all_files');
      const is_all_forms = frm.get('is_all_forms');
      const files = frm.get('files');
      const forms = frm.get('forms');

      if ((is_all_files && is_all_files.value) || (is_all_forms && is_all_forms.value) ||
        (files && files.value && files.value.length > 0) || (forms && forms.value && forms.value.length > 0)) {
          // we're all good nothing to do
      } else {
        result.fileFolderRequired = true;
      }

      // Make sure this step is attached to a tree for this validation to work correctly
      const path = frm.get('path');
      const path_variable = frm.get('path_variable');
      if (path && path_variable && !path_variable.value && (!path.value || path.value == '')) {
        result.pathRequired = true;
      }

      return (Object.keys(result).length == 0)? null: result;
    };
  }

  workflowUniqueNameValidator(): AsyncValidatorFn {

    return (control: AbstractControl): Observable<ValidationErrors> => {

      if (!control.parent)
        return of(null);

      // Make sure this step is attached to a tree for this validation to work correctly
      const id = (control.parent as FormGroup).get('id');

      // skip validation for existing workflows, name field is disabled anyways
      return (id && id.value)? of(null) : this.isNameUnique(control.value, (id)? id.value: '').pipe(
        map(() => { return null; }),
        catchError(() => of({unique: true})));
      }
  }

  execNameDynamicValidator(): ValidatorFn {

    return (frm: FormGroup): ValidationErrors => {

      let result: any = {};

      // Check Exec Name variable
      const is_workflow_exec_name_dynamic = frm.get('is_workflow_exec_name_dynamic');
      const workflow_exec_name_variable = frm.get('workflow_exec_name_variable');

      if (is_workflow_exec_name_dynamic && is_workflow_exec_name_dynamic.value &&
        workflow_exec_name_variable && !workflow_exec_name_variable.value) {
          result.execNameVariableRequired = true;
      }

      return (Object.keys(result).length == 0)? null: result;
    };
  }

  autoStartFormValidator(): ValidatorFn {

    return (frm: FormGroup): ValidationErrors => {

      let result: any = {};

      // Check Targets
      const is_entire_form = frm.get('is_entire_form');
      const initiator_workflow_form_name = frm.get('initiator_workflow_form_name');

      if (is_entire_form && is_entire_form.value == 1 &&
        initiator_workflow_form_name && (!initiator_workflow_form_name.value || initiator_workflow_form_name.value == '')) {
        initiator_workflow_form_name.setErrors({required: true});
      } else if (initiator_workflow_form_name) {
        let errors = initiator_workflow_form_name.errors;
        if (errors) {
          delete initiator_workflow_form_name.errors.required;
          errors = (Object.keys(errors).length == 0)? null: errors;
        }
        initiator_workflow_form_name.setErrors(errors);
      }

      return (Object.keys(result).length == 0)? null: result;
    };
  }

  autoStartSubmissionValueValidator(): ValidatorFn {

    return (frm: FormGroup): ValidationErrors => {

      let result: any = {};

      if (!frm.parent || !frm.parent.parent) {
        return null;
      }

      // Check Targets
      const is_entire_form = frm.parent.parent.get('is_entire_form');
      const is_required = frm.get('is_required');
      const exec_variable = frm.get('exec_variable');

      if (is_entire_form && is_entire_form.value == 0 && is_required && is_required.value == 1 &&
        exec_variable && !exec_variable.value) {
        exec_variable.setErrors({required: true});
      } else if (exec_variable) {
        let errors = exec_variable.errors;
        if (errors) {
          delete exec_variable.errors.required;
          errors = (Object.keys(errors).length == 0)? null: errors;
        }
        exec_variable.setErrors(errors);
      }

      return (Object.keys(result).length == 0)? null: result;
    };
  }
}